import { css } from "@emotion/react";
import type React from "react";
import { HeaderNavigationPane } from "../../../components/HeaderComponent/subcomponents/HeaderNavigationRightPane";
import { SwarovskiDropdown3 } from "../../../SwarovskiDropdown3";
import type { BundleProduct } from "./BundleProduct";
import { StockLevelIndicator } from "../PDP_SingleImageCarousel/price-bar/StockLevelIndicator";
import { SwarovskiButton } from "../../../components/_shared-components/ui-elements/SwarovskiButton";
import { FONTS, fontSize } from "../../../styles/fonts";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../../styles/styles-css-variables";
import cn from "classnames";
import { CSS_MQ } from "../../../styles/breakpoints";
import { type PickerData } from "../PickerData";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../../components/T";
import { DelayRendering, SmallLoadingSpinner } from "../../../routes/modal-loading-spinner";
import { usePrevious } from "../PDP_SingleImageCarousel/price-bar/usePreviousPrice";

type BundleContentsEditorProps = {
  products: Array<{
    product: BundleProduct;
    pickerData: PickerData;
  }>;
  isOpened: boolean;
  onClose: () => void;
  onBundleElementChanged: (_: { productKey: string; variantKey: string }) => void;
  priceLabel:
    | "loading"
    | "error"
    | undefined
    | {
        regular: string;
        discounted: string | undefined;
      };
  closeLabel: TranslatedValue;
  priceLoadingError: TranslatedValue;
};

export const BundleContentsEditor: React.FC<BundleContentsEditorProps> = ({
  products,
  onClose,
  isOpened,
  onBundleElementChanged,
  closeLabel,
  priceLabel,
  priceLoadingError,
}) => {
  const previousPrice = usePrevious(priceLabel);

  return (
    <div
      className={cn(`js-bundle-contents-editor`)}
      css={css`
        position: relative;
        z-index: 200;

        transition: transform 0.3s ease-in-out;

        ${CSS_MQ.atMostSm} {
          margin-bottom: ${CSS_SPACER_SIZES.md};
        }
      `}
    >
      <HeaderNavigationPane
        viewport="all"
        pane={
          isOpened
            ? {
                theme: "white",
                width: "standard",
              }
            : null
        }
        onClose={onClose}
        side="right"
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          {products.map(p => {
            return (
              <BundleConentProductEditor
                key={p.product.selectedVariant.sku}
                product={p.product}
                pickerData={p.pickerData}
                onBundleElementChanged={onBundleElementChanged}
              />
            );
          })}
        </div>

        {priceLabel && (
          <div
            css={css`
              margin-top: 40px;
              display: flex;
              align-items: baseline;
              gap: 12px;
            `}
          >
            {priceLabel === "loading" ? (
              <>
                {previousPrice && (
                  <BundlePriceLabel regular={previousPrice.regular} discounted={previousPrice.discounted} />
                )}

                <div
                  css={css`
                    display: inline-block;
                    margin-left: 8px;
                  `}
                >
                  <DelayRendering delayMs={500} fallback={null}>
                    <SmallLoadingSpinner color="green" size="1x" noMargin />
                  </DelayRendering>
                </div>
              </>
            ) : priceLabel === "error" ? (
              <T>{priceLoadingError}</T>
            ) : (
              <BundlePriceLabel {...priceLabel} />
            )}
          </div>
        )}

        <div
          css={css`
            margin-top: 12px;
          `}
        >
          <SwarovskiButton
            variant={"green"}
            as={"button"}
            onClick={onClose}
            css={css`
              width: 100%;
            `}
          >
            <T>{closeLabel}</T>
          </SwarovskiButton>
        </div>
      </HeaderNavigationPane>
    </div>
  );
};

const BundlePriceLabel: React.FC<{
  regular: string;
  discounted: string | undefined;
}> = ({ regular, discounted }) => {
  return (
    <>
      {discounted && (
        <span
          css={[
            css`
              text-decoration: line-through;
              color: ${CSS_COLORS.Green};
              opacity: 0.65;
            `,
            fontSize.x3,
          ]}
        >
          {regular}
        </span>
      )}

      <span
        css={[
          FONTS.SwarovskiFutura500,
          fontSize.x4,
          css`
            color: ${CSS_COLORS.Green};

            order: -1;
          `,
        ]}
        suppressHydrationWarning={true /* Money formatting */}
      >
        <>{discounted ?? regular}</>
      </span>
    </>
  );
};

const BundleConentProductEditor: React.FC<{
  product: BundleProduct;
  pickerData: PickerData;
  onBundleElementChanged: (_: { productKey: string; variantKey: string }) => void;
}> = ({ product, pickerData, onBundleElementChanged }) => {
  return (
    <div
      css={css`
        padding-top: 10px;
        // for a nice outline
        padding-left: 1px;
        padding-right: 1px;

        --bg-placeholder: var(--bg-color);
        --bg-disabled: ${CSS_COLORS.GrayLight2024};
        --bg-error: ${CSS_COLORS.GrayLight2024};
        --text-error: ${CSS_COLORS.RedCustom};
      `}
    >
      {pickerData.length > 0 ? (
        <SwarovskiDropdown3
          key={product.selectedVariant.key}
          values={pickerData.map(v => ({ label: v.label, value: v.key }))}
          onChange={variantKey => {
            onBundleElementChanged({ productKey: product.product.key, variantKey: variantKey });
          }}
          value={v => v}
          placeholder={product.product.name}
          required={true}
          autoComplete="off"
          fieldName="variant"
          state={product.selectedVariant.key}
          emptyValue={{ label: "Select variant", value: "" }}
          disabled={pickerData.length < 2}
        />
      ) : (
        <SwarovskiDropdown3
          key={product.selectedVariant.key}
          values={[{ label: "One size", value: "default" }]}
          onChange={v => {
            //
          }}
          value={v => "default"}
          placeholder={product.product.name}
          required={true}
          autoComplete="off"
          fieldName="variant"
          state={product.selectedVariant.key}
          emptyValue={{ label: "Select variant", value: "" }}
          disabled={true}
        />
      )}

      <span
        css={css`
          margin-top: 4px;
          display: block;
        `}
      >
        <StockLevelIndicator stockLevel={product.stockLevel} />
      </span>
    </div>
  );
};
